import {lazy, Suspense, useContext, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import AtomButton, {atomButtonColors, atomButtonDesigns, atomButtonShapes} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import useMountedState from '@s-ui/react-hooks/lib/useMountedState/index.js'
import {AuthenticationContext} from '@adv-mt/mt-context-authentication'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import {modalRelatedAdsLeadTargets} from '@adv-mt/mt-modal-related-ads'
import IconBin from '@adv-mt/mt-svg-icons/lib/Bin'
import IconCarAdd from '@adv-mt/mt-svg-icons/lib/CarCheck.js'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'
import {criteoTrackEvents, useCriteoTrack} from '@adv-mt/mt-tracking-use-criteo'
import withEventTrackHoc from '@adv-mt/mt-tracking-with-event-track-hoc'

const LazyModalRelatedAds = lazy(() =>
  import(/* webpackChunkName: "CardAdModalRelatedAds" */ '@adv-mt/mt-modal-related-ads')
)

const EVENT = 'Ad Unfavorited'

const AtomButtonWithEvent = withEventTrackHoc(AtomButton, EVENT)

const CardAdDeleted = ({ad, onToggleFav = () => {}, pageName}) => {
  const {domain, i18n} = useContext(Context)
  const {user: userData} = useContext(AuthenticationContext)
  const {track: criteoEventTrack} = useCriteoTrack()
  const {track: analyticsEventTrack} = useAnalyticsTrack()

  const config = domain.get('config')
  const isMobile = config.get('isMobile')
  const {TRACKING_EVENT_CONTEXTS, TRACKING_EVENT_NAMES} = config.get('tracking')

  const [recommendedAds, setRecommendedAds] = useState([])
  const [recommendedAdsMetadata, setRecommendedAdsMetadata] = useState()
  const [isOpenRecommendedAdsModal, setIsOpenRecommendedAdsModal] = useState(false)

  const getMountedState = useMountedState()
  const isMounted = getMountedState()

  const isHigherThanMobileResolution = useMediaQuery(useMediaQueryBreakpoints.sm, {
    defaultMatches: !isMobile
  })

  const {
    RECOMMENDED_ADS: {
      userFavoritesDeletedAd: {minRecommendations, maxRecommendations}
    }
  } = config.get('COMMON')

  const {HAS_RECOMMENDER: isRecommenderActive} = config.get('search')

  const hasRecommendedAds = Boolean(recommendedAds.length)

  const {id: adId} = ad

  // Just for a test purpose. After rollout/rollback we need to get this values from a exported config
  const contactType = isHigherThanMobileResolution ? 'email' : 'phoneCall'

  const getRecommendedAds = async ({adId, maxItems, context}) => {
    const [recommendedAdsError, recommendedAdsResponse] = await domain
      .get('get_recommended_ads_use_case')
      .execute({adId, maxItems})

    if (recommendedAdsError) return

    const {items, metadata} = recommendedAdsResponse

    if (items.length >= minRecommendations) {
      setRecommendedAds(items)
      setRecommendedAdsMetadata(metadata)
      const trackingData = await domain.get('get_event_tracking_data_use_case').execute({
        event: TRACKING_EVENT_NAMES.RECOMMENDATION_WIDGET_IMPRESSION_EVENT,
        context,
        ads: items,
        recommendation: metadata
      })
      analyticsEventTrack(trackingData)
    }
  }

  useEffect(() => {
    isRecommenderActive &&
      getRecommendedAds({
        adId: ad.id,
        maxItems: maxRecommendations,
        context: TRACKING_EVENT_CONTEXTS.FAVORITES_AD_DELETED
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleToggleFavorite = ev => {
    ev.preventDefault()
    domain
      .get('delete_user_favorites_use_case')
      .execute({adId})
      .then(() => {
        onToggleFav(false)
      })
  }

  const handleOpenRecommendedAdsModal = async () => {
    const trackingData = await domain.get('get_event_tracking_data_use_case').execute({
      event: TRACKING_EVENT_NAMES.RECOMMENDATION_WIDGET_VIEWABLE_IMPRESSION,
      context: TRACKING_EVENT_CONTEXTS.FAVORITES_AD_DELETED,
      ads: recommendedAds,
      recommendation: recommendedAdsMetadata
    })
    analyticsEventTrack(trackingData)

    if (userData) {
      setIsOpenRecommendedAdsModal(true)
    }
  }

  const handleCloseRecommendedAdsModal = () => setIsOpenRecommendedAdsModal(false)

  // Just for a test purpose. After rollout/rollback we need to get this values from a domain useCase
  const handleContactAdByMailFromModalRelatedAds = ({id, messageContactId, leadData}) => {
    analyticsEventTrack({
      event: 'Favorite Recommended Ad Replied Email',
      data: leadData
    })
    analyticsEventTrack({
      event: 'Lead Triggered',
      data: {
        lead_type: 'Favorite Recommended Ad Replied Email',
        hit_information3: messageContactId,
        ...leadData
      }
    })
    criteoEventTrack({
      event: criteoTrackEvents.TRACK_TRANSACTION,
      eventData: {adId: id}
    })
  }

  const handleOnToggleSimilarAdsModal = ({isOpen}) => {
    if (isOpen) {
      domain
        .get('get_page_tracking_data_use_case')
        .execute({
          page: pageName,
          pageType: 'similar-ads',
          advertisements: recommendedAds,
          contactType
        })
        .then(analyticsEventTrack)
    }
  }

  const handleClickRelatedAd = async ({ad, position}) => {
    const trackingData = await domain.get('get_event_tracking_data_use_case').execute({
      event: TRACKING_EVENT_NAMES.RECOMMENDATION_WIDGET_CLICKED,
      context: TRACKING_EVENT_CONTEXTS.FAVORITES_AD_DELETED,
      ads: [{ad, position}],
      recommendation: recommendedAdsMetadata
    })
    analyticsEventTrack(trackingData)
  }

  return (
    <div className="mt-CardAd-deleted">
      <div className="mt-CardAd-deletedContent">
        <p className="mt-CardAd-deletedText">
          {hasRecommendedAds ? i18n.t('CARD_AD_DELETED_RECOMMENDER_TEXT') : i18n.t('CARD_AD_DELETED_TEXT')}
        </p>
        <div className="mt-CardAd-deletedActions">
          {hasRecommendedAds && (
            <AtomButton
              color={atomButtonColors.ACCENT}
              fullWidth
              isFitted
              leftIcon={<IconCarAdd />}
              onClick={handleOpenRecommendedAdsModal}
              shape={atomButtonShapes.CIRCULAR}
            >
              {i18n.t('COMMON.SHOW_ADS')}
            </AtomButton>
          )}
          <AtomButtonWithEvent
            color={hasRecommendedAds ? atomButtonColors.NEUTRAL : atomButtonColors.ACCENT}
            design={hasRecommendedAds ? atomButtonDesigns.OUTLINE : atomButtonDesigns.SOLID}
            fullWidth
            isFitted
            leftIcon={<IconBin />}
            onClick={handleToggleFavorite}
            shape={atomButtonShapes.CIRCULAR}
          >
            {i18n.t('CARD_AD_DELETE_FAVORITE')}
          </AtomButtonWithEvent>
        </div>
      </div>
      {isMounted && isOpenRecommendedAdsModal && (
        <Suspense fallback={<></>}>
          <LazyModalRelatedAds
            ads={recommendedAds}
            contactType={contactType}
            leadTarget={modalRelatedAdsLeadTargets.favoriteRecommendedAds}
            message={i18n.t('FORM.CONTACT_MESSAGE_PLACEHOLDER')}
            modalDescription={i18n.t('COMMON.MODAL_RELATED_ADS_DESCRIPTION')}
            onContactAd={handleContactAdByMailFromModalRelatedAds}
            onClickRelatedAd={handleClickRelatedAd}
            onModalClose={handleCloseRecommendedAdsModal}
            onToggleModal={handleOnToggleSimilarAdsModal}
            showInitialNotification={false}
            showModal={isOpenRecommendedAdsModal}
            userData={userData}
          />
        </Suspense>
      )}
    </div>
  )
}

CardAdDeleted.displayName = 'CardAdDeleted'

CardAdDeleted.propTypes = {
  ad: PropTypes.object.isRequired,
  onToggleFav: PropTypes.func,
  pageName: PropTypes.string
}

export default CardAdDeleted
